import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import TimezoneSelector from '../../../components/TimezoneSelector';
import { adminService } from '../../../services/adminService';
import { isValidTimezone } from '../../../utils/timezones';

const OrganizationSetup = () => {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    supportEmail: '',
    timezone: '',
    ssoProvider: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    supportEmail: '',
    timezone: '',
    ssoProvider: '',
    settings: {
      allowSelfRegistration: true,
      requireEmailVerification: true,
      sessionDuration: 50
    },
    branding: {
      primaryColor: '#4F46E5',
      logo: ''
    }
  });

  useEffect(() => {
    loadOrganization();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadOrganization = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await adminService?.getOrganization();
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load organization');
        setOrganization(null); // Clear stale organization state
        return;
      }
      
      if (data) {
        setOrganization(data);
        setFormData({
          name: data?.name || '',
          domain: data?.domain || '',
          supportEmail: data?.supportEmail || '',
          timezone: data?.timezone || '',
          ssoProvider: data?.ssoProvider || '',
          settings: data?.settings || formData?.settings,
          branding: data?.branding || formData?.branding
        });
      } else {
        // No organization exists yet - clear state
        setOrganization(null);
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
      setOrganization(null); // Clear stale organization state on error
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    if (!email || email?.trim() === '') {
      return 'Support email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      return 'Please enter a valid email address';
    }
    if (email?.length > 255) {
      return 'Email address is too long (max 255 characters)';
    }
    return '';
  };

  const validateTimezone = (timezone) => {
    if (!timezone || timezone?.trim() === '') {
      return 'Timezone is required';
    }
    if (!isValidTimezone(timezone)) {
      return 'Please select a valid timezone from the list';
    }
    return '';
  };

  const validateSsoProvider = (provider) => {
    if (!provider || provider?.trim() === '') {
      return 'SSO provider is required';
    }
    const validProviders = ['azure', 'okta', 'google', 'saml'];
    if (!validProviders?.includes(provider)) {
      return 'Please select a valid SSO provider from the list';
    }
    return '';
  };

  const validateForm = () => {
    const errors = {
      supportEmail: validateEmail(formData?.supportEmail),
      timezone: validateTimezone(formData?.timezone),
      ssoProvider: validateSsoProvider(formData?.ssoProvider)
    };

    setFieldErrors(errors);

    return !errors?.supportEmail && !errors?.timezone && !errors?.ssoProvider;
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear success message when user starts editing
    if (successMessage) {
      setSuccessMessage('');
    }

    // Real-time validation for the changed field
    let error = '';
    if (field === 'supportEmail') {
      error = validateEmail(value);
    } else if (field === 'timezone') {
      error = validateTimezone(value);
    } else if (field === 'ssoProvider') {
      error = validateSsoProvider(value);
    }

    if (error !== fieldErrors?.[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Map camelCase to snake_case for database
      const saveData = {
        name: formData?.name,
        domain: formData?.domain,
        supportEmail: formData?.supportEmail,
        timezone: formData?.timezone,
        ssoProvider: formData?.ssoProvider,
        settings: formData?.settings,
        branding: formData?.branding
      };

      if (organization?.id) {
        // Update existing organization
        const { data: updatedOrg, error: updateError } = await adminService?.updateOrganization(organization?.id, saveData);
        
        if (updateError) {
          setError(updateError?.message || 'Failed to save organization settings');
          // If organization not found, clear the stale state
          if (updateError?.message?.includes('not found')) {
            setOrganization(null);
          }
          return;
        }
        
        // Update local state with the saved organization
        if (updatedOrg) {
          setOrganization(updatedOrg);
        }
      } else {
        // Create new organization
        const { data: newOrg, error: createError } = await adminService?.createOrganization(saveData);
        
        if (createError) {
          setError(createError?.message || 'Failed to create organization');
          return;
        }
        
        // Update local state with the newly created organization
        if (newOrg) {
          setOrganization(newOrg);
        }
      }
      
      setSuccessMessage('Organization settings saved successfully!');
      // Reload to ensure we have the latest data
      await loadOrganization();
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadOrganization}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const ssoProviders = [
    { value: "azure", label: "Azure Active Directory" },
    { value: "okta", label: "Okta" },
    { value: "google", label: "Google Workspace" },
    { value: "saml", label: "Generic SAML 2.0" }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBrandingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      branding: {
        ...prev?.branding,
        [field]: value
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Success Message Banner */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Error Message Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError('')}
            className="text-red-600 hover:text-red-800"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Building2" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold">Organization Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Organization Name"
            type="text"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            required
          />

          <Input
            label="Domain"
            type="text"
            value={formData?.domain}
            onChange={(e) => handleChange('domain', e?.target?.value)}
            description="Used for email validation"
            required
          />

          <div className="space-y-1">
            <Input
              label="Support Email"
              type="email"
              value={formData?.supportEmail}
              onChange={(e) => handleFieldChange('supportEmail', e?.target?.value)}
              required
              className={fieldErrors?.supportEmail ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors?.supportEmail && (
              <div className="flex items-start gap-1.5 text-red-600">
                <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                <p className="text-xs">{fieldErrors?.supportEmail}</p>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <TimezoneSelector
              label="Timezone"
              value={formData?.timezone}
              onChange={(value) => handleFieldChange('timezone', value)}
              required
              error={fieldErrors?.timezone}
            />
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Shield" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold">SSO Configuration</h3>
        </div>

        <div className="space-y-4">
          <div className="space-y-1">
            <Select
              label="SSO Provider"
              options={ssoProviders}
              value={formData?.ssoProvider}
              onChange={(value) => handleFieldChange('ssoProvider', value)}
              description="Enterprise authentication provider"
              required
              className={fieldErrors?.ssoProvider ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {fieldErrors?.ssoProvider && (
              <div className="flex items-start gap-1.5 text-red-600">
                <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                <p className="text-xs">{fieldErrors?.ssoProvider}</p>
              </div>
            )}
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">SSO Configuration Steps</p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Configure your identity provider with Cwtch metadata</li>
                  <li>Add authorized redirect URIs to your SSO application</li>
                  <li>Test authentication flow with a test user account</li>
                  <li>Enable SSO for all organization members</li>
                </ol>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => window.open('https://docs.cwtch.com/sso', '_blank')}
          >
            View SSO Documentation
          </Button>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Palette" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold">Branding Customization</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Primary Brand Color"
            type="color"
            value={formData?.branding?.primaryColor}
            onChange={(e) => handleBrandingChange('primaryColor', e?.target?.value)}
            description="Used for buttons and accents"
          />

          <Input
            label="Logo URL"
            type="url"
            value={formData?.branding?.logo}
            onChange={(e) => handleBrandingChange('logo', e?.target?.value)}
            description="Recommended size: 200x200px"
          />
        </div>

        <div className="mt-4 p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-3">Logo Preview</p>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg border-2 border-border overflow-hidden bg-background">
              <img 
                src={formData?.branding?.logo} 
                alt="Organization logo preview showing company branding"
                className="w-full h-full object-cover"
                onError={(e) => e.target.src = '/assets/images/no_image.png'}
              />
            </div>
            <div>
              <p className="text-sm font-medium">{formData?.name}</p>
              <p className="text-xs text-muted-foreground">{formData?.domain}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline">
          Cancel
        </Button>
        <Button
          variant="default"
          iconName="Save"
          iconPosition="left"
          loading={saving}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default OrganizationSetup;